(function () {
  // Track initialized Glider instances for cleanup, keyed by the live
  // `.quick-add-option-carousel` container element.
  const initializedSwipers = new Map();
  let resizeObserver = null;
  let lastWidth = window.innerWidth;
  const MOBILE_BREAKPOINT = 1023;

  function destroyAllSwipers() {
    initializedSwipers.forEach((glider, el) => {
      // Capture sibling lookups BEFORE destroy(): Glider's destroy() clones `el` and
      // replaces it in the DOM, detaching the original — el.closest(...) after that
      // call would return null.
      const wrapper = el.closest('.quick-add-option-carousel-wrapper');
      const prevButton = wrapper?.querySelector('.quick-add-option-prev');
      const nextButton = wrapper?.querySelector('.quick-add-option-next');

      glider.destroy();

      prevButton?.classList.add('hidden');
      nextButton?.classList.add('hidden');
    });
    initializedSwipers.clear();
  }

  // Size (and other non-Color) options aren't carousels — they use a CSS grid inside a
  // native overflow-x scroll container. Wire their prev/next arrows to scroll that
  // container directly, and hide the arrows when there's nothing to scroll.
  function initScrollNav() {
    const scrollers = document.querySelectorAll('.quick-add-option-carousel[data-option-type="size"]');
    scrollers.forEach((el) => {
      const wrapper = el.closest('.quick-add-option-carousel-wrapper');
      const prevButton = wrapper?.querySelector('.quick-add-option-prev');
      const nextButton = wrapper?.querySelector('.quick-add-option-next');
      if (!prevButton || !nextButton) return;

      // Toggle arrow visibility based on whether the row actually overflows.
      const needsScroll = el.scrollWidth > el.clientWidth + 1;
      prevButton.classList.toggle('hidden', !needsScroll);
      nextButton.classList.toggle('hidden', !needsScroll);
      if (!needsScroll) return;

      // Bind click handlers once per element (survives resize re-runs).
      if (el.dataset.scrollNavBound) return;
      el.dataset.scrollNavBound = 'true';

      // Scroll one button per click. Step = distance between adjacent slides (button
      // width + gap); fall back to a single button's width if there's only one.
      const step = () => {
        const slides = el.querySelectorAll('.quick-add-option-slide');
        if (slides.length > 1) return slides[1].offsetLeft - slides[0].offsetLeft;
        return slides[0]?.offsetWidth || el.clientWidth * 0.8;
      };
      prevButton.addEventListener('click', () => el.scrollBy({ left: -step(), behavior: 'smooth' }));
      nextButton.addEventListener('click', () => el.scrollBy({ left: step(), behavior: 'smooth' }));
    });
  }

  function initSwipers() {
    // Skip carousel initialization on desktop (> breakpoint)
    if (window.innerWidth > MOBILE_BREAKPOINT) {
      destroyAllSwipers();
      return;
    }

    // Wire the native-scroll (size) carousels' arrows before the Glider-backed ones.
    initScrollNav();

    // `:not(.glider)` mirrors the old Swiper "already initialized" guard — Glider adds
    // the `glider` class in its constructor and destroy() strips it back off the clone
    // that replaces a torn-down container, so this reliably means "not currently an
    // active Glider instance".
    const swiperElements = document.querySelectorAll(
      '.quick-add-option-carousel:not(.glider):not([data-option-type="size"])'
    );
    if (!swiperElements.length) return;

    // Now initialize each carousel
    swiperElements.forEach((el) => {
      // Skip if already initialized
      if (initializedSwipers.has(el)) return;

      const wrapper = el.closest('.quick-add-option-carousel-wrapper');
      const prevButton = wrapper?.querySelector('.quick-add-option-prev');
      const nextButton = wrapper?.querySelector('.quick-add-option-next');
      const swiperWrapper = el.querySelector('.quick-add-option-track');
      if (!wrapper || !prevButton || !nextButton || !swiperWrapper) return;

      // Bind arrow clicks ONCE per button. Buttons are siblings of
      // `.quick-add-option-carousel` inside the wrapper, so unlike the carousel container
      // they're never replaced by Glider's destroy() — they persist across every
      // destroy/reinit cycle. Do NOT use Glider's own `arrows` option (pins its click
      // handler to the button permanently on first bind — after any destroy+reinit it
      // would keep driving a detached first-generation instance) OR its `scrollItem()`
      // (its animation duration is `opt.duration * pixel-distance`, tuned for full-width
      // slides — at our ~44px single-swatch step that's ~22ms, i.e. an instant jump, not
      // a smooth scroll). Native `scrollBy({behavior:'smooth'})` gives a real, consistent
      // animation regardless of step size, matching the size-row's step() below. Re-resolve
      // the CURRENT live container + its Glider instance at click time (see destroy() note
      // above for why `el` itself can't be captured in the closure).
      if (!prevButton.dataset.gliderArrowBound) {
        prevButton.dataset.gliderArrowBound = 'true';
        prevButton.addEventListener('click', () => {
          const liveEl = wrapper.querySelector('.quick-add-option-carousel');
          const glider = initializedSwipers.get(liveEl);
          if (glider) liveEl.scrollBy({ left: -glider.itemWidth, behavior: 'smooth' });
        });
      }
      if (!nextButton.dataset.gliderArrowBound) {
        nextButton.dataset.gliderArrowBound = 'true';
        nextButton.addEventListener('click', () => {
          const liveEl = wrapper.querySelector('.quick-add-option-carousel');
          const glider = initializedSwipers.get(liveEl);
          if (glider) liveEl.scrollBy({ left: glider.itemWidth, behavior: 'smooth' });
        });
      }

      // Measure if content overflows the container (before Glider touches anything).
      const containerWidth = el.offsetWidth;
      const contentWidth = swiperWrapper.scrollWidth;
      const needsCarousel = contentWidth > containerWidth;

      // Hide nav buttons and skip init if no overflow
      if (!needsCarousel) {
        prevButton.classList.add('hidden');
        nextButton.classList.add('hidden');
        return;
      }

      // Show nav buttons and initialize Glider
      prevButton.classList.remove('hidden');
      nextButton.classList.remove('hidden');

      // Measure the real on-screen pitch between adjacent slides (natural width + any
      // gap/margin) — same idiom as initScrollNav's step() above. Slides keep their own
      // natural/CSS width here (mirroring the old Swiper `slidesPerView: 'auto'`), not a
      // hardcoded design width like assets/carousel.js's fixed-slidesToShow carousels.
      const slides = swiperWrapper.querySelectorAll('.quick-add-option-slide');
      const itemWidth =
        slides.length > 1
          ? slides[1].offsetLeft - slides[0].offsetLeft
          : slides[0]?.offsetWidth || containerWidth;

      const glider = new Glider(el, {
        slidesToShow: 'auto',
        // Fixed at 1 (not 'auto'): with 'auto' Glider resolves this to
        // floor(slidesToShow) — how many swatches fit in the visible row at once
        // (~7-8 here) — so each arrow click would jump a whole "page" instead of
        // moving one swatch at a time.
        slidesToScroll: 1,
        scrollLock: true,
        skipTrack: true,
        draggable: true,
        itemWidth,
        exactWidth: true,
      });

      initializedSwipers.set(el, glider);
    });
  }

  function setupResizeObserver() {
    if (resizeObserver) return;

    resizeObserver = new ResizeObserver((entries) => {
      const currentWidth = window.innerWidth;
      const crossedBreakpoint =
        (lastWidth <= MOBILE_BREAKPOINT && currentWidth > MOBILE_BREAKPOINT) ||
        (lastWidth > MOBILE_BREAKPOINT && currentWidth <= MOBILE_BREAKPOINT);

      if (crossedBreakpoint) {
        lastWidth = currentWidth;
        initSwipers();
      }
    });

    // Observe the body for resize changes
    resizeObserver.observe(document.body);
  }

  window.initQuickAddModalSwipersB = function () {
    initSwipers();
    setupResizeObserver();
  };

  // Initialize on first load if content already present
  document.addEventListener('DOMContentLoaded', window.initQuickAddModalSwipersB);
})();
